import { useState } from "react";
import { Box, Typography, Select, Option, ToggleButtonGroup, Button as JoyButton, Chip } from "@mui/joy";
import { ChevronDown } from "lucide-react";

import { ControlledTextField } from "../../shared";
import type { BillingCycle, IBillingSelection } from "../../interfaces";
import BillingModeCard from "./BillingModeCard";
import { BILLING_MODES, DEFAULT_BILLING_SELECTION, TRIAL_LENGTH_OPTIONS } from "./constants";

interface IBillingProps {
    /** Omit to let the component hold its own state. */
    value?: IBillingSelection;
    onChange?: (value: IBillingSelection) => void;
    disabled?: boolean;
}

export default function Billing({ value, onChange, disabled }: IBillingProps) {
    const [internal, setInternal] = useState<IBillingSelection>(DEFAULT_BILLING_SELECTION);
    const selection = value ?? internal;

    const update = (patch: Partial<IBillingSelection>) => {
        const next = { ...selection, ...patch };
        if (!value) setInternal(next);
        onChange?.(next);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                <Typography level="title-md">Billing mode</Typography>
                <Chip size="sm" variant="soft" color="warning">
                    Not saved yet
                </Chip>
            </Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                Select a mode, then fill in the details for that plan.
            </Typography>

            <Box
                role="radiogroup"
                aria-label="Billing mode"
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.25,
                }}
            >
                {BILLING_MODES.map((option) => (
                    <BillingModeCard
                        key={option.value}
                        option={option}
                        selected={selection.Mode === option.value}
                        disabled={disabled}
                        onSelect={(mode) => update({ Mode: mode })}
                    />
                ))}
            </Box>

            <Box
                sx={{
                    mt: 1.5,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "4px",
                    bgcolor: "background.level1",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {selection.Mode === "TRIAL" && (
                    <FieldRow label="Trial length" hint="Access ends when the trial expires.">
                        <Select
                            size="sm"
                            value={selection.TrialDays}
                            disabled={disabled}
                            indicator={<ChevronDown size={16} />}
                            onChange={(_, next) => next && update({ TrialDays: next })}
                            sx={selectSx}
                        >
                            {TRIAL_LENGTH_OPTIONS.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </FieldRow>
                )}

                {selection.Mode === "SUBSCRIPTION" && (
                    <>
                        <FieldRow label="Billing cycle" hint="Annual plans are invoiced once per year.">
                            <ToggleButtonGroup
                                size="sm"
                                value={selection.Cycle}
                                disabled={disabled}
                                onChange={(_, next) => next && update({ Cycle: next as BillingCycle })}
                                sx={{ "--ButtonGroup-radius": "4px" }}
                            >
                                <JoyButton value="MONTHLY">Monthly</JoyButton>
                                <JoyButton value="ANNUAL">Annual</JoyButton>
                            </ToggleButtonGroup>
                        </FieldRow>

                        <FieldRow label="Included seats" hint="Users allowed before overage applies.">
                            <ControlledTextField
                                value={selection.Seats}
                                disabled={disabled}
                                type="number"
                                placeholder="5"
                                onChange={(next) => update({ Seats: next })}
                                sx={{ maxWidth: 160 }}
                            />
                        </FieldRow>

                        <FieldRow label="Rate per seat" hint="Charged each billing cycle.">
                            <ControlledTextField
                                value={selection.RatePerSeat}
                                disabled={disabled}
                                type="number"
                                placeholder="0.00"
                                startDecorator="$"
                                onChange={(next) => update({ RatePerSeat: next })}
                                sx={{ maxWidth: 160 }}
                            />
                        </FieldRow>
                    </>
                )}

                {selection.Mode === "PER_PROJECT" && (
                    <FieldRow label="Rate per project" hint="Applied when a project is created.">
                        <ControlledTextField
                            value={selection.RatePerProject}
                            disabled={disabled}
                            type="number"
                            placeholder="0.00"
                            startDecorator="$"
                            onChange={(next) => update({ RatePerProject: next })}
                            sx={{ maxWidth: 160 }}
                        />
                    </FieldRow>
                )}

                {selection.Mode === "CUSTOM" && (
                    <FieldRow label="Contract reference" hint="Agreement or PO number for manual invoicing.">
                        <ControlledTextField
                            value={selection.ContractReference}
                            disabled={disabled}
                            placeholder="e.g., MSA-2026-014"
                            onChange={(next) => update({ ContractReference: next })}
                            sx={{ maxWidth: 260 }}
                        />
                    </FieldRow>
                )}

                <FieldRow label="Billing contact" hint="Where invoices and billing notices are sent.">
                    <ControlledTextField
                        value={selection.BillingEmail}
                        disabled={disabled}
                        type="email"
                        placeholder="billing@example.com"
                        onChange={(next) => update({ BillingEmail: next })}
                        sx={{ maxWidth: 260 }}
                    />
                </FieldRow>
            </Box>
        </Box>
    );
}

const selectSx = {
    maxWidth: 160,
    borderRadius: "4px",
    boxShadow: "none",
    bgcolor: "background.surface",
};

function FieldRow({
    label,
    hint,
    children,
}: {
    label: string;
    hint: string;
    children: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
                <Typography level="body-xs" color="neutral">
                    {hint}
                </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>{children}</Box>
        </Box>
    );
}
